import { useState } from "react";
import { CalendarX, FileCheck, Printer, UserPlus } from "lucide-react";
import { Link, useParams } from "react-router";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";

import { VoltDataTable, type VoltColumn } from "../ui/VoltDataTable";
import { VoltEmpty } from "../ui/VoltEmpty";
import { VoltPageHeader } from "../ui/VoltPageHeader";
import { VoltStatusBadge } from "../ui/VoltStatusBadge";
import {
  salesName,
  useAddPerson,
  useBillableSession,
  useDecideLateRequest,
  useDistrictSales,
  useSessionClosing,
  useSessionDetail,
  useSetBillable,
  useSetStatus,
  useVoltRoles,
  type SalesPerson,
} from "./attendanceApi";
import {
  auditText,
  berlinMonth,
  countEntries,
  entryOrigin,
  formatDate,
  formatDateTime,
  formatLongDate,
  formatTime,
  goesToBv,
  isOpenLateRequest,
  KIND_LABELS,
  monthName,
  STATUS_LABELS,
  STATUS_MAP,
} from "./attendanceLogic";
import { AttendanceAccess, AttendanceFrame } from "./AttendanceFrame";
import { ReasonDialog } from "./ReasonDialog";
import type {
  AttendanceAudit,
  AttendanceEntry,
  AttendanceMember,
  AttendanceSession,
  AttendanceStatus,
} from "./types";

type Detail = NonNullable<ReturnType<typeof useSessionDetail>["data"]>;

const SETTABLE: AttendanceStatus[] = ["present", "excused", "absent"];

const StatusPicker = ({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) => (
  <div className="flex flex-col gap-1.5">
    <Label>Status</Label>
    <ToggleGroup
      type="single"
      variant="outline"
      value={value}
      onValueChange={(v) => v && onChange(v)}
      className="w-full"
    >
      {SETTABLE.map((s) => (
        <ToggleGroupItem key={s} value={s} className="flex-1">
          {STATUS_LABELS[s]}
        </ToggleGroupItem>
      ))}
    </ToggleGroup>
  </div>
);

const Counter = ({ label, value }: { label: string; value: number }) => (
  <Card className="py-3">
    <CardContent className="px-4">
      <div className="text-2xl font-semibold tabular-nums">{value}</div>
      <div className="text-sm text-muted-foreground">{label}</div>
    </CardContent>
  </Card>
);

const CorrectDialog = ({
  entry,
  name,
  onClose,
}: {
  entry: AttendanceEntry | null;
  name: string;
  onClose: () => void;
}) => {
  const setStatus = useSetStatus();
  const [status, setStatusValue] = useState("");
  return (
    <ReasonDialog
      open={!!entry}
      onOpenChange={(open) => {
        if (!open) onClose();
        setStatusValue("");
      }}
      title={`Status von ${name} korrigieren`}
      description="Jede Korrektur landet mit Grund in der Prüfspur."
      submitLabel="Speichern"
      canSubmit={!!status && status !== entry?.status}
      onSubmit={(reason) =>
        setStatus.mutateAsync({ entryId: entry!.id, status, reason })
      }
    >
      <StatusPicker value={status} onChange={setStatusValue} />
    </ReasonDialog>
  );
};

const LateRequests = ({
  requests,
  name,
}: {
  requests: AttendanceEntry[];
  name: (id: number) => string;
}) => {
  const decide = useDecideLateRequest();
  const [decision, setDecision] = useState<{
    entry: AttendanceEntry;
    decision: "confirm" | "reject";
  } | null>(null);
  if (requests.length === 0) return null;
  return (
    <section className="mb-6" aria-labelledby="late-requests">
      <h2 id="late-requests" className="mb-2 font-semibold">
        Offene Nachträge
      </h2>
      <ul className="flex flex-col gap-2">
        {requests.map((e) => (
          <li
            key={e.id}
            className="flex flex-wrap items-center gap-3 rounded-md border p-3"
          >
            <div className="min-w-0 flex-1">
              <div className="font-medium">{name(e.sales_id)}</div>
              <div className="text-sm text-muted-foreground">
                angefragt {formatDateTime(e.requested_at!)}
                {e.note ? ` · „${e.note}“` : ""}
              </div>
            </div>
            <Button
              size="sm"
              onClick={() => setDecision({ entry: e, decision: "confirm" })}
            >
              Bestätigen
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setDecision({ entry: e, decision: "reject" })}
            >
              Ablehnen
            </Button>
          </li>
        ))}
      </ul>
      <ReasonDialog
        open={!!decision}
        onOpenChange={(open) => !open && setDecision(null)}
        title={
          decision?.decision === "confirm"
            ? `Nachtrag von ${name(decision.entry.sales_id)} bestätigen`
            : `Nachtrag von ${decision ? name(decision.entry.sales_id) : ""} ablehnen`
        }
        description={
          decision?.decision === "confirm"
            ? "Die Person gilt dann als anwesend."
            : "Die Person gilt dann als abwesend."
        }
        submitLabel={
          decision?.decision === "confirm" ? "Bestätigen" : "Ablehnen"
        }
        onSubmit={(reason) =>
          decide.mutateAsync({
            entryId: decision!.entry.id,
            decision: decision!.decision,
            reason,
          })
        }
      />
    </section>
  );
};

const AddPersonDialog = ({
  open,
  onOpenChange,
  session,
  listed,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  session: AttendanceSession;
  listed: Set<number>;
}) => {
  const sales = useDistrictSales(open ? session.district : null);
  const addPerson = useAddPerson();
  const [salesId, setSalesId] = useState("");
  const [status, setStatus] = useState("present");
  const candidates = (sales.data ?? []).filter(
    (s) => !s.disabled && !listed.has(s.id),
  );
  return (
    <ReasonDialog
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o);
        setSalesId("");
        setStatus("present");
      }}
      title="Person hinzufügen"
      description="Für Personen, die nicht im Kalender eingeladen waren."
      submitLabel="Hinzufügen"
      canSubmit={!!salesId}
      onSubmit={(reason) =>
        addPerson.mutateAsync({
          sessionId: session.id,
          salesId: Number(salesId),
          status,
          reason,
        })
      }
    >
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="attendance-person">Person</Label>
        <Select value={salesId} onValueChange={setSalesId}>
          <SelectTrigger id="attendance-person" className="w-full">
            <SelectValue
              placeholder={
                sales.isPending ? "Lädt …" : "Person aus der Fraktion wählen"
              }
            />
          </SelectTrigger>
          <SelectContent>
            {candidates.map((s) => (
              <SelectItem key={s.id} value={String(s.id)}>
                {salesName(s)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {sales.data && candidates.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Alle aktiven Personen der Fraktion stehen schon auf der Liste.
          </p>
        ) : null}
      </div>
      <StatusPicker value={status} onChange={setStatus} />
    </ReasonDialog>
  );
};

const BillableBox = ({ session }: { session: AttendanceSession }) => {
  const month = berlinMonth(session.starts_at);
  const billable = useBillableSession(session.district, month);
  const setBillable = useSetBillable();
  const [open, setOpen] = useState(false);
  if (session.kind !== "small" || billable.isPending) return null;
  const isBillable = billable.data === session.id;
  const name = monthName(month);
  return (
    <>
      <Alert className="mb-6" variant={isBillable ? "default" : undefined}>
        <AlertTitle>
          {isBillable
            ? `Diese Sitzung geht im ${name} an die BV.`
            : `Im ${name} geht eine andere Freitagssitzung an die BV.`}
        </AlertTitle>
        <AlertDescription>
          <p>
            Je Monat wird eine Vorstandssitzung gemeldet. Vorausgewählt ist die
            erste, bei der alle Eingeladenen da waren, sonst die erste nicht
            abgesagte.
          </p>
          {!isBillable && !session.cancelled ? (
            <Button
              size="sm"
              variant="outline"
              className="mt-2"
              onClick={() => setOpen(true)}
            >
              Stattdessen diese melden
            </Button>
          ) : null}
        </AlertDescription>
      </Alert>
      <ReasonDialog
        open={open}
        onOpenChange={setOpen}
        title={`Diese Sitzung für den ${name} melden`}
        description={`Die bisher gemeldete Sitzung im ${name} wird dann nicht mehr gemeldet.`}
        submitLabel="Melden"
        onSubmit={(reason) =>
          setBillable.mutateAsync({ sessionId: session.id, reason })
        }
      />
    </>
  );
};

const AuditTrail = ({
  audit,
  name,
}: {
  audit: AttendanceAudit[];
  name: (id: number | null) => string;
}) => (
  <section aria-labelledby="audit-trail" className="mt-8">
    <h2 id="audit-trail" className="mb-1 font-semibold">
      Prüfspur
    </h2>
    <p className="mb-3 text-sm text-muted-foreground">
      Jede Änderung mit Zeit, Person und Grund. Sie wird nicht gelöscht und ist
      die Grundlage für die Meldung an die BV.
    </p>
    {audit.length === 0 ? (
      <p className="text-sm text-muted-foreground">Noch keine Einträge.</p>
    ) : (
      <ol className="flex flex-col gap-2 text-sm">
        {[...audit].reverse().map((a) => (
          <li key={a.id} className="flex flex-wrap gap-x-3 border-b pb-2">
            <time
              dateTime={a.at}
              className="w-28 shrink-0 text-muted-foreground tabular-nums"
            >
              {formatDateTime(a.at)}
            </time>
            <div className="min-w-0 flex-1">
              <div>{auditText(a, name)}</div>
              {a.reason ? (
                <div className="text-muted-foreground">Grund: {a.reason}</div>
              ) : null}
            </div>
            <div className="text-muted-foreground">
              {a.actor_sales_id ? name(a.actor_sales_id) : "Kalenderabgleich"}
            </div>
          </li>
        ))}
      </ol>
    )}
  </section>
);

const SessionDetail = ({ detail }: { detail: Detail }) => {
  const { session, entries, audit, members, sales } = detail;
  const [correcting, setCorrecting] = useState<AttendanceEntry | null>(null);
  const [adding, setAdding] = useState(false);
  const billable = useBillableSession(
    session.district,
    berlinMonth(session.starts_at),
  );
  const toBv = goesToBv(session, billable.data);
  const closing = useSessionClosing(session.id, toBv);

  const salesById = new Map<number, SalesPerson>(sales.map((s) => [s.id, s]));
  const name = (id: number | null) => {
    const s = id ? salesById.get(id) : undefined;
    return s ? salesName(s) : "Unbekannt";
  };
  const fnById = new Map<number, AttendanceMember>(
    members.map((m) => [m.sales_id, m]),
  );
  const requests = entries.filter((e) => isOpenLateRequest(e, audit));
  const listedRows = entries.filter((e) => !requests.includes(e));
  const counts = countEntries(entries);

  const columns: VoltColumn<AttendanceEntry>[] = [
    { key: "name", header: "Name", cell: (e) => name(e.sales_id) },
    {
      key: "function",
      header: "Funktion",
      cell: (e) => fnById.get(e.sales_id)?.function || "–",
    },
    {
      key: "status",
      header: "Status",
      cell: (e) => (
        <div className="flex items-center gap-2">
          <VoltStatusBadge status={e.status} map={STATUS_MAP} />
          {session.cancelled ? null : (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setCorrecting(e)}
              aria-label={`Status von ${name(e.sales_id)} korrigieren`}
            >
              Korrigieren
            </Button>
          )}
        </div>
      ),
    },
    {
      key: "origin",
      header: "Woher",
      cell: (e) => (
        <span className="text-muted-foreground">{entryOrigin(e, audit)}</span>
      ),
    },
  ];

  return (
    <>
      <VoltPageHeader
        title={session.title}
        description={`${formatLongDate(session.starts_at)}, ${formatTime(session.starts_at)}–${formatTime(session.ends_at)}${session.location ? ` · ${session.location}` : ""} · ${KIND_LABELS[session.kind]}`}
        action={
          session.cancelled ? null : (
            <div className="flex flex-wrap gap-2">
              {toBv && closing.data ? (
                <Button asChild>
                  <Link to={`/attendance/closings/${closing.data.id}`}>
                    <FileCheck aria-hidden />
                    Monatsabschluss öffnen
                  </Link>
                </Button>
              ) : null}
              <Button variant="outline" asChild>
                <Link
                  to={`/attendance/sessions/${session.id}/qr`}
                  target="_blank"
                >
                  <Printer aria-hidden />
                  QR drucken
                </Link>
              </Button>
              <Button onClick={() => setAdding(true)}>
                <UserPlus aria-hidden />
                Person hinzufügen
              </Button>
            </div>
          )
        }
      />
      {session.cancelled ? (
        <Alert className="mb-6">
          <CalendarX aria-hidden />
          <AlertTitle>Diese Sitzung ist abgesagt.</AlertTitle>
          <AlertDescription>
            Sie geht nicht an die BV; Korrekturen sind gesperrt.
          </AlertDescription>
        </Alert>
      ) : null}
      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Counter label="anwesend" value={counts.present} />
        <Counter label="entschuldigt" value={counts.excused} />
        <Counter label="abwesend" value={counts.absent} />
        <Counter label="offen" value={counts.open} />
      </div>
      <BillableBox session={session} />
      <LateRequests requests={requests} name={(id) => name(id)} />
      <VoltDataTable
        rows={listedRows}
        columns={columns}
        rowKey={(e) => e.id}
        empty={
          <p className="text-sm text-muted-foreground">
            Noch niemand eingeladen.
          </p>
        }
      />
      <AuditTrail audit={audit} name={name} />
      <CorrectDialog
        entry={correcting}
        name={correcting ? name(correcting.sales_id) : ""}
        onClose={() => setCorrecting(null)}
      />
      <AddPersonDialog
        open={adding}
        onOpenChange={setAdding}
        session={session}
        listed={new Set(entries.map((e) => e.sales_id))}
      />
    </>
  );
};

export const AttendanceSessionPage = () => {
  const { id } = useParams();
  const sessionId = Number(id);
  const { ready } = useVoltRoles();
  const detail = useSessionDetail(sessionId);
  const back = detail.data
    ? `/attendance?month=${berlinMonth(detail.data.session.starts_at)}`
    : "/attendance";
  return (
    <AttendanceFrame
      title={
        detail.data ? formatDate(detail.data.session.starts_at) : "Sitzung"
      }
      back={back}
    >
      <AttendanceAccess>
        {() =>
          !ready || detail.isPending ? (
            <Skeleton className="h-40 w-full" />
          ) : detail.error ? (
            <p role="alert" className="text-sm text-destructive">
              Die Sitzung konnte nicht geladen werden.
            </p>
          ) : !detail.data ? (
            <VoltEmpty
              icon={CalendarX}
              title="Sitzung nicht gefunden"
              description="Es gibt sie nicht, oder sie gehört zu einer anderen Fraktion."
              action={
                <Button variant="outline" asChild>
                  <Link to="/attendance">Zur Übersicht</Link>
                </Button>
              }
            />
          ) : (
            <SessionDetail detail={detail.data} />
          )
        }
      </AttendanceAccess>
    </AttendanceFrame>
  );
};

AttendanceSessionPage.path = "/attendance/sessions/:id";
